import { useCallback, useMemo, useState } from 'react';

const DEFAULT_SHOW_MORE_THRESHOLD = 14;
const MINIMUM_TO_DISPLAY_INITIALLY = 2;
const MINIMUM_EXTRA_TO_DISPLAY = 2;

const useMarketDisplayLimit = <T>(
  data: T[],
  sessionKey: string,
  threshold = DEFAULT_SHOW_MORE_THRESHOLD
): [data: T[], showRemainingData: () => void, isLimited: boolean] => {
  /**
   * Calculates the number of items to show when the show more button is visible.
   * Calculation is based off of 4 being visible when the threshold is 6 and
   * 10 being visible when the threshold is 14.
   */
  const quantityWhenLimited = Math.max(
    Math.floor(threshold * 0.75 - 0.5),
    MINIMUM_TO_DISPLAY_INITIALLY
  );
  const [isDataLimited, setIsDataLimited] = useState(
    data.length >= threshold && data.length - quantityWhenLimited >= MINIMUM_EXTRA_TO_DISPLAY
  );

  const dataToDisplay = useMemo(
    () => (isDataLimited ? data.slice(0, quantityWhenLimited) : data),
    [data, isDataLimited, quantityWhenLimited]
  );

  const showRemainingData = useCallback(() => {
    setIsDataLimited(false);
  }, [setIsDataLimited]);

  return [dataToDisplay, showRemainingData, isDataLimited];
};

export default useMarketDisplayLimit;
